import React from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import SEO from '../../components/SEO/SEO';
import './BlogPost.css';

const POSTS = {
  1: {
    title: '10 Tips to Teach Boat Guests',
    image: '/unimar-boat-fishing.webp',
    date: 'March 10, 2026',
    category: 'Safety',
    body: [
      'Taking guests out on your Unimar boat is one of the great joys of boat ownership. But with that comes responsibility — your guests may have little or no boating experience, so it falls on you to make sure everyone on board is safe.',
      '1. Give a safety briefing before departure. Show everyone where life jackets are stored, how to put one on, and when to wear them.',
      '2. Point out the emergency equipment. Fire extinguisher, flares, first aid kit — guests should know where these are.',
      '3. Explain the rules around the boat. No sitting on the bow when moving, no dangling feet over the side at speed.',
      '4. Make sure everyone knows the "man overboard" signal. A shout and a point — everyone keeps eyes on the person in the water.',
      '5. Limit alcohol until anchored. This one speaks for itself.',
      '6. Assign a co-captain. Brief one guest on how to use the radio and call for help if you are incapacitated.',
      '7. Explain the swim ladder. Sounds simple — it is not obvious to first-timers how to use it from the water.',
      '8. Brief guests on engine awareness. Stay clear of the propeller at all times.',
      '9. Sunscreen and hydration. First-timers underestimate how intense sun and wind on the water can be.',
      '10. Have fun — a relaxed, confident captain puts guests at ease. When you are calm and prepared, everyone enjoys the day.',
    ],
  },
  2: {
    title: 'Family Adventures on the Water',
    image: '/unimar-boat-family.webp',
    date: 'March 5, 2026',
    category: 'Lifestyle',
    body: [
      'There is nothing quite like spending a day on the water with your family. The combination of fresh air, beautiful scenery, and the gentle motion of the boat creates memories that last a lifetime.',
      'When planning a family day out, start early. Mornings on the water are calmer, cooler, and the kids will be at their most energetic.',
      'Pack more food and water than you think you need. Sea air builds appetite, and it is always better to have too much than too little.',
      'Choose anchorages with calm, shallow water for families with younger children. A sandy bottom and clear water make for perfect swimming conditions.',
      'Bring snorkelling gear. Even basic masks and fins open up a completely different world for children, and it keeps them entertained for hours.',
      'Plan the route around the kids energy levels, not the adults. A shorter trip with more stops beats a long passage with restless children.',
      'The Unimar STIA 580 is an excellent family boat — spacious, stable, and with plenty of room to configure seating and sunbed layouts to suit your crew.',
    ],
  },
  3: {
    title: 'Cruising the Adriatic: A Guide',
    image: '/unimar-boat-cruising.webp',
    date: 'February 28, 2026',
    category: 'Travel',
    body: [
      'The Adriatic Sea is one of the most beautiful cruising grounds in the world. Crystal-clear turquoise water, medieval walled towns, deserted islands, and excellent seafood — it has everything a boater could want.',
      'The Croatian coast is the obvious starting point. Split and Dubrovnik are the major hubs, but the real gems are the islands: Hvar, Brač, Vis, Korčula. Each has its own character, anchorages, and restaurants.',
      'The Montenegrin coast is less visited but equally stunning. Kotor Bay is one of the most dramatic natural harbours in Europe.',
      'If you are based in Macedonia, the route south through Albania and into the Greek islands via Corfu is increasingly popular. The Albanian Riviera remains largely undiscovered.',
      'Weather planning is essential. The Adriatic can produce strong winds quickly — particularly the Bora (northeast) and Jugo (south/southeast). Always check forecasts before departure.',
      'The best cruising season runs from May through October. July and August are the busiest and most expensive. June and September offer the best balance of good weather and fewer crowds.',
      'A well-equipped Unimar boat with the right navigation and safety equipment is perfectly suited for Adriatic cruising.',
    ],
  },
  4: {
    title: 'Choosing the Right Boat for You',
    image: '/unimar-home-banner.webp',
    date: 'February 20, 2026',
    category: 'Buying Guide',
    body: [
      'Buying a boat is one of the most exciting decisions you will make — and also one where it pays to take your time. The right boat for your neighbour may be completely wrong for you.',
      'Start by being honest about how you will use it. Fishing with friends requires different features to family day trips or coastal cruising. Many buyers end up with a boat suited to their aspirations rather than their reality.',
      'Consider where you will keep it. A marina berth, a trailer at home, or a dry stack facility all have different implications for boat size and weight.',
      'The Unimar Manta Ray 420 is ideal for those wanting a compact, easy-to-handle first boat. It trailers easily, is economical to run, and is perfect for coastal day trips and light fishing.',
      'The Unimar Real 500 steps things up — more space, more comfort, and capable of handling choppier conditions. A great all-rounder for families and anglers alike.',
      'The Unimar STIA 580 is the flagship. At 5.76 metres with full configurability — seating, tops, electronics, engines — it is built for those who want the best. Stable, spacious, and beautifully made.',
      'Use our online configurator to spec your perfect boat. It takes just a few minutes and gives you a clear picture of exactly what you are getting — and what it costs.',
    ],
  },
};

function BlogPost() {
  const { id } = useParams();
  const post = POSTS[id];

  if (!post) {
    return (
      <div className="blogpost-page">
        <Header />
        <div className="blogpost-page__not-found">
          <h1>Post not found</h1>
          <a href="/blog">← Back to Blog</a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="blogpost-page">
      <SEO
        title={post.title}
        description={post.body[0].slice(0, 155)}
        url={`/blog/${id}`}
        image={post.image}
        type="article"
      />
      <Header />
      <div className="blogpost-page__hero" style={{ backgroundImage: `url(${post.image})` }}>
        <div className="blogpost-page__hero-overlay" />
        <div className="blogpost-page__hero-content">
          <span className="blogpost-page__category">{post.category}</span>
          <h1 className="blogpost-page__title">{post.title}</h1>
          <span className="blogpost-page__date">{post.date}</span>
        </div>
      </div>
      <div className="blogpost-page__content">
        <a href="/blog" className="blogpost-page__back">← Back to Blog</a>
        <article className="blogpost-page__article">
          {post.body.map((para, i) => (
            <p key={i} className="blogpost-page__para">{para}</p>
          ))}
        </article>
        <div className="blogpost-page__cta">
          <a href="/configurator" className="blogpost-page__cta-btn">Build Your Boat</a>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default BlogPost;
